import { useEffect } from "react"
import { useFormContext } from "react-hook-form"

import Button from "~/v2/components/ui/button"
import FormField from "~/v2/components/vaas/form-field"
import type { VaasCreateValues, VaasEditValues } from "~/v2/types/vaas"

const AI_CREATED_BY_VALUES = ["modified-ai", "created-ai"]
type VaasFormValues = VaasCreateValues | VaasEditValues

const VaasAiMetadataFields = () => {
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext<VaasFormValues>()
  const createdBy = watch("created_by")
  const creationTimestamp = watch("vaas_creation_timestamp")
  const providerName = watch("vaas_provider_name")
  const systemVersion = watch("vaas_system_version")
  const showAiFields = AI_CREATED_BY_VALUES.includes(String(createdBy))

  useEffect(() => {
    if (showAiFields) {
      return
    }

    if (providerName) {
      setValue("vaas_provider_name", "", { shouldDirty: true })
    }
    if (systemVersion) {
      setValue("vaas_system_version", "", { shouldDirty: true })
    }
    if (creationTimestamp) {
      setValue("vaas_creation_timestamp", "", { shouldDirty: true })
    }
  }, [creationTimestamp, providerName, setValue, showAiFields, systemVersion])

  return (
    <div className="grid gap-4">
      <label className="flex flex-col gap-2">
        <span className="text-sm font-medium text-foreground">Created by</span>
        <select
          className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
          value={createdBy || ""}
          {...register("created_by", {
            setValueAs: (value) => value || null,
          })}
        >
          <option value="">None</option>
          <option value="human">Human</option>
          <option value="modified-ai">Modified AI</option>
          <option value="created-ai">Created AI</option>
        </select>
        {errors.created_by?.message && (
          <span className="text-xs text-destructive">
            {String(errors.created_by.message)}
          </span>
        )}
      </label>

      {showAiFields && (
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            description='For example, "OpenAI".'
            label="Provider name"
            name="vaas_provider_name"
          />
          <FormField
            description='For example, "DALL-E 3.5".'
            label="System version"
            name="vaas_system_version"
          />
          <div className="md:col-span-2">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <FormField
                  label="Creation timestamp"
                  name="vaas_creation_timestamp"
                  type="datetime-local"
                />
              </div>
              {creationTimestamp && (
                <Button
                  className="mt-7"
                  onClick={() =>
                    setValue("vaas_creation_timestamp", "", {
                      shouldDirty: true,
                    })
                  }
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VaasAiMetadataFields
